import { useState } from 'react';
import { Check, ArrowRight, AlertTriangle, X, HardDrive } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { TierUpgradeModal } from '@/components/TierUpgradeModal';
import { TierDowngradeModal } from '@/components/TierDowngradeModal';
import { CancelReversalModal } from '@/components/CancelReversalModal';
import { AddStorageModal } from '@/components/AddStorageModal';
import { useToast } from '@/components/ToastProvider';
import { useAuth, useTier } from '@/hooks/useAuth';
import { subscriptionApi } from '@/lib/subscription';
import type { SubscriptionTier } from '@maker-toolchest/shared';
import { TIER_LIMITS } from '@maker-toolchest/shared';
import { useStorageStats } from '@/hooks/useStorageStats';

const TIER_NAMES: Record<SubscriptionTier, string> = {
  TINKERER: 'Tinkerer',
  HOBBYIST: 'Hobbyist',
  BUSINESS: 'Business',
};

const TIER_PRICES: Record<SubscriptionTier, string> = {
  TINKERER: 'Free',
  HOBBYIST: '$12/month',
  BUSINESS: '$49/month',
};

const TIER_ORDER: SubscriptionTier[] = ['TINKERER', 'HOBBYIST', 'BUSINESS'];

/**
 * Format bytes to human-readable format
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes === Infinity) return 'Unlimited';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

export function SubscriptionPage() {
  const { currentTenant } = useAuth();
  const { tier } = useTier();
  const toast = useToast();
  const { data: storageStats } = useStorageStats();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isDowngradeModalOpen, setIsDowngradeModalOpen] = useState(false);
  const [isReversalModalOpen, setIsReversalModalOpen] = useState(false);
  const [isAddStorageModalOpen, setIsAddStorageModalOpen] = useState(false);
  const [downgradeTargetTier, setDowngradeTargetTier] = useState<SubscriptionTier | null>(null);

  const currentTier = tier || 'TINKERER';
  const scheduledTier = currentTenant?.scheduledTier || null;
  const tierChangeDate = currentTenant?.tierChangeDate ? new Date(currentTenant.tierChangeDate) : null;
  const scheduledChangeType = currentTenant?.scheduledChangeType || null;

  const currentTierIndex = TIER_ORDER.indexOf(currentTier);
  const nextTier = currentTierIndex < TIER_ORDER.length - 1
    ? TIER_ORDER[currentTierIndex + 1]
    : null;
  const isAtHighestTier = !nextTier;
  const isOnFreeTier = currentTier === 'TINKERER';
  const hasScheduledChange = !!scheduledTier;
  // Handle both explicit CANCEL type and legacy data (null type + TINKERER target)
  const hasPendingCancellation = scheduledChangeType === 'CANCEL' ||
    (scheduledTier === 'TINKERER' && scheduledChangeType === null);
  const hasPendingDowngrade = hasScheduledChange && scheduledChangeType === 'DOWNGRADE';

  const upgradeMutation = useMutation({
    mutationFn: () => subscriptionApi.upgradeTier(),
    onSuccess: () => {
      toast.success('Subscription upgraded successfully');
      window.location.reload();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upgrade subscription');
    },
  });

  const downgradeMutation = useMutation({
    mutationFn: (input: { targetTier: SubscriptionTier; reason?: string }) =>
      subscriptionApi.downgradeTier(input),
    onSuccess: () => {
      toast.success('Downgrade scheduled successfully');
      window.location.reload();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to schedule downgrade');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (reason?: string) => subscriptionApi.cancelSubscription({ reason }),
    onSuccess: () => {
      toast.success('Subscription cancellation scheduled');
      window.location.reload();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to cancel subscription');
    },
  });

  const cancelScheduledChangeMutation = useMutation({
    mutationFn: () => subscriptionApi.cancelScheduledChange(),
    onSuccess: () => {
      toast.success('Scheduled change cancelled');
      window.location.reload();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to cancel scheduled change');
    },
  });

  const handleUpgradeClick = () => {
    if (nextTier) {
      setIsUpgradeModalOpen(true);
    }
  };

  const handleUpgradeConfirm = () => {
    upgradeMutation.mutate();
  };

  const handleDowngradeClick = (targetTier: SubscriptionTier) => {
    setDowngradeTargetTier(targetTier);
    setIsDowngradeModalOpen(true);
  };

  const handleDowngradeConfirm = (reason?: string) => {
    if (!downgradeTargetTier) return;

    if (downgradeTargetTier === 'TINKERER') {
      cancelMutation.mutate(reason);
    } else {
      downgradeMutation.mutate({ targetTier: downgradeTargetTier, reason });
    }
  };

  const handleCancelScheduledChange = () => {
    cancelScheduledChangeMutation.mutate();
  };

  const handleReverseCancellation = () => {
    setIsReversalModalOpen(true);
  };

  const handleReversalConfirm = () => {
    cancelScheduledChangeMutation.mutate();
    setIsReversalModalOpen(false);
  };

  const currentLimits = TIER_LIMITS[currentTier];
  const formatLimit = (limit: number): string => {
    return limit === Infinity ? 'Unlimited' : limit.toString();
  };

  const formattedChangeDate = tierChangeDate?.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  // Calculate effective date for downgrade (30 days from now in dev)
  const effectiveDate = new Date();
  effectiveDate.setDate(effectiveDate.getDate() + 30);

  return (
    <div className="p-4">
      {/* Page header */}
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-foreground">Subscription</h1>
        <p className="text-sm text-foreground-muted">Manage your plan and billing</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Subscription Cancellation Alert */}
        {hasPendingCancellation && tierChangeDate && (
          <div className="rounded-lg border border-error bg-error/5 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-error" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-error">
                  Subscription Cancellation Scheduled
                </h3>
                <p className="mt-1 text-sm text-foreground">
                  Your subscription is scheduled to be cancelled on{' '}
                  <strong>{formattedChangeDate}</strong>. After this date, your account will be
                  moved to the free {TIER_NAMES.TINKERER} plan.
                </p>
                <p className="mt-2 text-sm text-foreground-muted">
                  You can reverse this cancellation at any time before the effective date to keep
                  your {TIER_NAMES[currentTier]} plan.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelScheduledChange}
                disabled={cancelScheduledChangeMutation.isPending}
                className="flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {!cancelScheduledChangeMutation.isPending && (
              <div className="mt-3 ml-8">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleReverseCancellation}
                  loading={cancelScheduledChangeMutation.isPending}
                >
                  Keep My {TIER_NAMES[currentTier]} Plan
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Scheduled Downgrade Alert */}
        {hasPendingDowngrade && scheduledTier && tierChangeDate && (
          <div className="rounded-lg border border-warning bg-warning/5 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-warning" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-warning">
                  Scheduled Plan Change
                </h3>
                <p className="mt-1 text-sm text-foreground">
                  Your plan will change to <strong>{TIER_NAMES[scheduledTier]}</strong> on{' '}
                  <strong>{formattedChangeDate}</strong>. You'll continue to have full access to your{' '}
                  {TIER_NAMES[currentTier]} plan until that date.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelScheduledChange}
                disabled={cancelScheduledChangeMutation.isPending}
                className="flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {!cancelScheduledChangeMutation.isPending && (
              <div className="mt-3 ml-8">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCancelScheduledChange}
                  loading={cancelScheduledChangeMutation.isPending}
                >
                  Keep {TIER_NAMES[currentTier]} Plan
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Current Plan */}
        <div className="rounded border border-primary bg-primary/5 p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-sm font-medium text-foreground">
                Current Plan: {TIER_NAMES[currentTier]}
              </h3>
              <p className="text-xs text-foreground-muted">{TIER_PRICES[currentTier]}</p>
            </div>
            {!isAtHighestTier && !hasScheduledChange && (
              <Button
                size="sm"
                variant="primary"
                onClick={handleUpgradeClick}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                Upgrade
              </Button>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-foreground">
                <Check className="h-4 w-4 text-primary" />
                <span>Printers</span>
              </div>
              <span className="font-medium text-foreground">
                {formatLimit(currentLimits.printers)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-foreground">
                <Check className="h-4 w-4 text-primary" />
                <span>Filaments</span>
              </div>
              <span className="font-medium text-foreground">
                {formatLimit(currentLimits.filaments)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-foreground">
                <Check className="h-4 w-4 text-primary" />
                <span>Team Members</span>
              </div>
              <span className="font-medium text-foreground">
                {formatLimit(currentLimits.teamMembers)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-foreground">
                {currentLimits.hasProjects ? (
                  <Check className="h-4 w-4 text-primary" />
                ) : (
                  <span className="h-4 w-4 text-foreground-muted">—</span>
                )}
                <span>Project Management</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-foreground">
                {currentLimits.hasCostTracking ? (
                  <Check className="h-4 w-4 text-primary" />
                ) : (
                  <span className="h-4 w-4 text-foreground-muted">—</span>
                )}
                <span>Cost Tracking</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-foreground">
                <Check className="h-4 w-4 text-primary" />
                <span>Filament Tracking</span>
              </div>
              <span className="font-medium text-foreground capitalize">
                {currentLimits.filamentTracking}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-foreground">
                <Check className="h-4 w-4 text-primary" />
                <span>Storage</span>
              </div>
              <span className="font-medium text-foreground">
                {currentLimits.storageMB === Infinity
                  ? 'Unlimited'
                  : currentLimits.storageMB >= 1024
                    ? `${currentLimits.storageMB / 1024} GB`
                    : `${currentLimits.storageMB} MB`}
              </span>
            </div>
          </div>
        </div>

        {/* Storage Usage */}
        {storageStats && (
          <div className="rounded border border-border bg-background-subtle p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-foreground-muted" />
                <h3 className="text-sm font-medium text-foreground">Storage Usage</h3>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsAddStorageModalOpen(true)}
              >
                Add Storage
              </Button>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Used</span>
                <span className="font-medium text-foreground">{formatBytes(storageStats.usedBytes)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Limit</span>
                <span className="font-medium text-foreground">{formatBytes(storageStats.limitBytes)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground-muted">Available</span>
                <span className="font-medium text-foreground">
                  {formatBytes(storageStats.limitBytes - storageStats.usedBytes)}
                </span>
              </div>
              {/* Progress bar */}
              <div className="pt-2">
                <div className="w-full bg-background rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${Math.min(storageStats.percentUsed, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-foreground-muted mt-1 text-right">
                  {storageStats.percentUsed}% used
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Available Plans */}
        {!isAtHighestTier && !hasScheduledChange && (
          <div className="rounded border border-border bg-background-subtle p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">Available Plans</h3>
            <div className="space-y-3">
              {TIER_ORDER.slice(currentTierIndex + 1).map((tierKey) => {
                const tierLimits = TIER_LIMITS[tierKey];
                return (
                  <div
                    key={tierKey}
                    className="rounded border border-border bg-background p-3 hover:border-primary transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-medium text-foreground">
                          {TIER_NAMES[tierKey]}
                        </h4>
                        <p className="text-xs text-foreground-muted">{TIER_PRICES[tierKey]}</p>
                      </div>
                      {tierKey === nextTier && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={handleUpgradeClick}
                        >
                          Select Plan
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-foreground-muted">
                      <span>{formatLimit(tierLimits.printers)} printers</span>
                      <span>{formatLimit(tierLimits.filaments)} filaments</span>
                      <span>{formatLimit(tierLimits.teamMembers)} team members</span>
                      <span>
                        {tierLimits.storageMB === Infinity
                          ? 'Unlimited'
                          : tierLimits.storageMB >= 1024
                            ? `${tierLimits.storageMB / 1024} GB`
                            : `${tierLimits.storageMB} MB`}{' '}
                        storage
                      </span>
                      {tierLimits.hasProjects && <span>Projects</span>}
                      {tierLimits.hasCostTracking && <span>Cost tracking</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Development Notice */}
        {import.meta.env.DEV && (
          <div className="rounded border border-warning bg-warning/5 p-4">
            <h3 className="text-sm font-medium text-warning mb-2">Development Mode</h3>
            <p className="text-sm text-foreground-muted">
              You're running in development mode. Plan changes will be processed immediately or scheduled for 30 days from now. In production, this will integrate with Stripe for secure payment handling.
            </p>
          </div>
        )}

        {/* Downgrade/Cancel Section */}
        {!isOnFreeTier && !hasScheduledChange && (
          <div className="rounded border border-border bg-background-subtle p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">Change Plan</h3>
            <div className="space-y-3">
              {/* Downgrade Options */}
              {TIER_ORDER.slice(0, currentTierIndex).reverse().map((tierKey) => {
                const isCancel = tierKey === 'TINKERER';
                return (
                  <div
                    key={tierKey}
                    className="flex items-center justify-between rounded border border-border bg-background p-3"
                  >
                    <div>
                      <h4 className="text-sm font-medium text-foreground">
                        {isCancel ? 'Cancel Subscription' : `Downgrade to ${TIER_NAMES[tierKey]}`}
                      </h4>
                      <p className="text-xs text-foreground-muted">
                        {isCancel
                          ? 'Move to free tier at renewal'
                          : `${TIER_PRICES[tierKey]} • Effective at renewal`}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDowngradeClick(tierKey)}
                    >
                      {isCancel ? 'Cancel' : 'Downgrade'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Confirmation Modal */}
      {nextTier && (
        <TierUpgradeModal
          open={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
          onConfirm={handleUpgradeConfirm}
          currentTier={currentTier}
          targetTier={nextTier}
          loading={upgradeMutation.isPending}
        />
      )}

      {/* Downgrade/Cancel Confirmation Modal */}
      {downgradeTargetTier && (
        <TierDowngradeModal
          open={isDowngradeModalOpen}
          onClose={() => {
            setIsDowngradeModalOpen(false);
            setDowngradeTargetTier(null);
          }}
          onConfirm={handleDowngradeConfirm}
          currentTier={currentTier}
          targetTier={downgradeTargetTier}
          effectiveDate={effectiveDate}
          loading={downgradeMutation.isPending || cancelMutation.isPending}
        />
      )}

      {/* Cancellation Reversal Modal */}
      <CancelReversalModal
        open={isReversalModalOpen}
        onClose={() => setIsReversalModalOpen(false)}
        onConfirm={handleReversalConfirm}
        currentTier={currentTier}
        loading={cancelScheduledChangeMutation.isPending}
      />

      {/* Add Storage Modal */}
      <AddStorageModal
        open={isAddStorageModalOpen}
        onClose={() => setIsAddStorageModalOpen(false)}
        onUpgrade={nextTier && !hasScheduledChange ? handleUpgradeClick : undefined}
        currentTier={currentTier}
        nextTier={nextTier}
      />
    </div>
  );
}
